import React from 'react';
import { motion } from 'framer-motion';

/**
 * HeroSection
 * - variant="image": background photo with lighter overlay (per team: "lighten the overlay")
 * - variant="services": solid #4B1180 background, white text — used for Services pages (Business, Education, Courses, More About Consulting) AND Contact/Donate
 * - variant="about": photo background with very light overlay, white H1 — used for About-related pages (Mission, Insights)
 */
const HeroSection = ({
  title,
  subtitle,
  backgroundImage,
  variant = 'image',
  overlay = true,
  overlayOpacity = 0.35, // Lightened from 0.6 per team feedback
  height = 'h-56 md:h-64',
  textColor = 'text-white',
  children,
}) => {
  // Services variant — solid purple, no image
  if (variant === 'services') {
    return (
      <section className={`relative ${height} flex items-center justify-center overflow-hidden bg-[#4B1180]`}>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {title && (
              <h1 className="text-4xl font-semibold uppercase leading-[1.05] text-white md:text-5xl">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-3 max-w-3xl mx-auto text-base text-white/90 md:text-lg">
                {subtitle}
              </p>
            )}
            {children}
          </motion.div>
        </div>
      </section>
    );
  }

  // Default and "about" variants — image background with lightened overlay
  return (
    <section className={`relative ${height} flex items-center justify-center overflow-hidden`}>
      {backgroundImage && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}
      {!backgroundImage && (
        <div className="absolute inset-0 bg-[#4B1180]" />
      )}

      {/* Lightened overlay — was 0.6, now 0.35 default; even lighter on About-style heroes */}
      {overlay && backgroundImage && (
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: variant === 'about' ? 0.25 : overlayOpacity }}
        />
      )}

      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {title && (
            <h1 className={`text-4xl font-semibold uppercase leading-[1.05] md:text-5xl ${textColor}`}>
              {title}
            </h1>
          )}
          {subtitle && (
            <p className={`mt-3 max-w-3xl mx-auto text-base md:text-lg ${textColor}`}>
              {subtitle}
            </p>
          )}
          {children}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
